import React from "react";
import { useTranslations } from "next-intl";
import { TourSummary } from "@/types/dto/pdp/pdp-tour-summary";
import { Box, Stack, Typography } from "@mui/material";
import { ColletteButton } from "@collette-travel/collette-common-controls";
import { removeDash, stripTags } from "@/utils/content-utils";
import ActivityLevelIndicator from "@/components/common/activity-level-indicator/activity-level-indicator";


interface IsThisTourForMeProps {
  tourSummaryData?: TourSummary;
  activityLevelShortDescription: string;
  activityLevelPhysicalRequirement: string;
  travelStyleNames: string;
  travelStyleDescriptions: string;
}

const IsThisTourForMe = ({
  tourSummaryData,
  activityLevelShortDescription,
  activityLevelPhysicalRequirement,
  travelStyleNames,
  travelStyleDescriptions
}: IsThisTourForMeProps) => {
  const t = useTranslations("PDP");

  const handleScroll = () => {
    const element = document.getElementById("essential-tour-information");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <Stack 
      data-testid="is-this-tour-for-me-container"
      sx={{
        width: "100%",
        gap: 3
      }}
    >
      {/* Heading */}
      <Typography
        data-testid="essential-tour-heading"
        variant="h4"        
      >
        {t("Essential Tour Information")}
      </Typography>

      {/* Group Size Section */}
      <Box data-testid="group-size-section">
        <Typography
          data-testid="group-size-heading"
          variant="h6"
          sx={{ fontSize: "1.8rem", fontWeight: "600 !important", lineHeight: "normal !important", mb: "0.8rem" }}
        >
          {t("Group Size")}
        </Typography>
        <Typography
          data-testid="group-size-description"
          sx={{ fontSize: "1.6rem", fontWeight: 400, fontStyle: "italic" }}
        >
            {t("Average")} {t("travelers")}: {tourSummaryData?.groupSize}
        </Typography>
      </Box>

      {/* Activity Level Section */}
      <Box data-testid="activity-level-section">
        <Typography
          data-testid="activity-level-heading"
          variant="h6"
          sx={{ fontSize: "1.8rem", fontWeight: "600 !important", lineHeight: "normal !important", mb: "0.8rem" }}
        >
          {t("Activity Level")} {tourSummaryData?.activityLevel} -{" "}
          {stripTags(removeDash(tourSummaryData?.activityLevelDescription ?? ""))}
        </Typography>
        <Box sx={{ mb: "1.2rem" }}>
          <ActivityLevelIndicator
            activityLevelNumber={tourSummaryData?.activityLevel || 1}
            size="medium"
          />
        </Box>
        <Typography
          data-testid="activity-level-description"
          sx={{ fontSize: "1.6rem", fontWeight: 400, fontStyle: "italic" }}
        >
          {stripTags(removeDash(activityLevelShortDescription))}
        </Typography>
      </Box>

      {/* Physical Requirement Section */}
      {activityLevelPhysicalRequirement && (
        <Box data-testid="physical-requirement-section">
          <Typography
            data-testid="physical-requirement-heading"
            variant="h6"
            sx={{ fontSize: "1.8rem", fontWeight: "600 !important", lineHeight: 1, mb: "0.8rem" }}
          >
            {t("Physical Requirements")}
          </Typography>
          <Typography
            data-testid="physical-requirement-description"
            sx={{ fontSize: "1.6rem", fontWeight: 400, fontStyle: "italic" }}
          >
            {stripTags(removeDash(activityLevelPhysicalRequirement))}
          </Typography>
        </Box>
      )}

      {/* Travel Style Section */}
      <Box data-testid="travel-style-section">
        <Typography
          data-testid="travel-style-heading"
          variant="h6"
          sx={{ fontSize: "1.8rem", fontWeight: "600 !important", lineHeight: 1, mb: "0.8rem" }}
        >
          {t("Travel Style")} - {travelStyleNames}
        </Typography>
        <Typography
          data-testid="travel-style-description"
          sx={{ fontSize: "1.6rem", fontWeight: 400, fontStyle: "italic" }}
        >
          {stripTags(removeDash(travelStyleDescriptions))}
        </Typography>
      </Box>

      {/* Button */}
      <ColletteButton
        variant="outlined"
        size="large"
        label={t("Is This Tour Right For Me?")}
        testId="btn-is-this-tour-right-for-me"
        data-track="click"
        onClick={handleScroll}
        sx={{
          margin: "0 auto",
          mt: 1
        }}
      />

    </Stack>
  );
};

export default IsThisTourForMe;
